import type { Store, Effect } from '@rxsv/core';
import { createStore, combineEffects } from '@rxsv/core';
import { withDevTools } from '@rxsv/tools';
import { devToolsOpen, withPoorMansReduxDevTools } from '@bayer-int/portletce-shared';
import { Subject, asyncScheduler } from 'rxjs';
import { switchMap, observeOn } from 'rxjs/operators';
import { identity } from 'lodash-es';

import type { RootService } from 'portlet/config/service';
import { isProd, isTest } from 'portlet/config/env';

import type { ExternalAppAction, PublicAction } from './actions';
import { AppAction } from './actions';
import type { AppState } from './reducer';
import { reducer } from './reducer';
import type { AppEffect, EffectFactory } from './effects';
import { effectFactories } from './effects';
import { Selectors } from './selectors';
import { TriggersAndActionsCommunicationInterface } from './triggersAndActions/TriggersAndActionsCommunicationInterface';

export type AppStore = Store<AppAction, AppState>;
export type AppStoreInterface = { triggersAndActions: TriggersAndActionsCommunicationInterface };
export { AppAction, Selectors };
export type { AppState, AppEffect, EffectFactory, ExternalAppAction, PublicAction };

/** Runs the effects, if called subsequently it will ignore pending actions from existing effect and only handle new ones  */
type StoreInitializer = (service: RootService) => void;
type StoreDeinitializer = () => void;

export const buildStore = (
    name = 'portletce',
): [AppStore, AppStoreInterface, StoreInitializer, StoreDeinitializer] => {
    // do not use redux devtools in prod builds if they are not opened (for performance reasons),
    // do not use dev tools during tests at all
    const devTools: (s: AppStore) => AppStore = isTest
        ? identity
        : !isProd || (isProd && devToolsOpen())
          ? withDevTools
          : withPoorMansReduxDevTools(name);
    const store = devTools(createStore<AppAction, AppState>(reducer, undefined, name));
    const storeInterface: AppStoreInterface = {
        triggersAndActions: new TriggersAndActionsCommunicationInterface(store.action$),
    };

    /**
     * NOTE: section below implements effect reinitialization.
     * It solves cases when we want to ignore all pending async actions from effects (which are yet to come),
     * but continue the epic to listen on new incoming ones.
     *
     * Idea was heavily inspired by
     * https://redux-observable.js.org/docs/recipes/HotModuleReplacement.html
     *
     * and follows the implementation of the store. Note we are no longer using `store.effect$.next`.
     * We manage pushing actions back to the stream ourselves.
     * https://github.com/grzegorz-bielski/rxsv/blob/master/packages/core/src/creators/createStore.ts
     */
    const effect$ = new Subject<Effect<AppAction, AppState>>();

    const effectSubscription = effect$
        .pipe(
            // using `switchMap`, because new instances of effects should cause pending
            // actions to be ignored
            switchMap(effect =>
                effect(store.action$, store.state$).pipe(observeOn(asyncScheduler)),
            ),
        )
        .subscribe(action => store.action$.next(action));

    return [
        store,
        storeInterface,
        rootService => {
            const effects = effectFactories.map(fn => fn(rootService));
            const rootEffect = combineEffects<AppAction, AppState>(...effects);

            effect$.next(rootEffect);
        },
        () => {
            effect$.complete();
            effectSubscription.unsubscribe();
        },
    ];
};
